import { useStore } from 'effector-react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { handleScroll } from '../helpers';

import { TItemsWithPagination, TParams } from '../types';

import {
  $currentDataWithoutFiltersStore,
  $filtersStore,
  saveCurrentPaginationPage,
  saveCurrentQueries,
  setIsLabelRender,
} from 'src/models';

type TProps = {
  onClick: (obj: TParams) => void;
  onBtnLoadClick: (obj: TParams) => void;
  callback?: (queries: string[], q?: string) => Promise<TItemsWithPagination>;
  callbackWithId?: (id: string, queries: string[], q?: string) => Promise<TItemsWithPagination>;
  setPage?: (page: number) => void;
  currSort?: string;
  additionalQuery?: string;
  id?: string;
  productId?: string;
  q?: string;
};

export const usePagination = ({
  id,
  productId,
  q,
  currSort,
  additionalQuery,
  onClick,
  onBtnLoadClick,
  callback,
  callbackWithId,
  setPage,
}: TProps) => {
  const {
    filtersQueries,
    switchQueries,
    priceQueries,
    currentPaginationPage: currentPaginationPageWithFilters,
  } = useStore($filtersStore);
  const store = useStore($currentDataWithoutFiltersStore);

  const currentPageStoreKey =
    store[globalThis.location.pathname.split('/').at(-1)! + (q ? `?q=${q}` : '')];

  const currentPaginationPage = setPage
    ? currentPageStoreKey?.page ?? 1
    : currentPaginationPageWithFilters;

  const [isLoadBtnClick, setIsLoadBtnClick] = useState(false);

  const sortQuery = useMemo(() => (currSort ? `sort=${currSort}` : ''), [currSort]);

  const handlePrevClick = useCallback(() => {
    const queries = [
      sortQuery,
      `page=${currentPaginationPage - 1}`,
      filtersQueries,
      switchQueries,
      priceQueries,
      additionalQuery ?? '',
    ];

    saveCurrentQueries(queries);

    onClick({
      productId,
      id,
      q,
      queries,
      fn: callback,
      fnWithId: callbackWithId,
    });
    if (setPage) {
      setPage(currentPaginationPage - 1);
    } else {
      saveCurrentPaginationPage(currentPaginationPage - 1);
    }
    setIsLoadBtnClick(false);
    setIsLabelRender(false);
  }, [
    currentPaginationPage,
    sortQuery,
    id,
    productId,
    q,
    additionalQuery,
    onClick,
    callback,
    callbackWithId,
  ]);

  const handleNextClick = useCallback(() => {
    const queries = [
      sortQuery,
      `page=${currentPaginationPage + 1}`,
      filtersQueries,
      switchQueries,
      priceQueries,
      additionalQuery ?? '',
    ];

    saveCurrentQueries(queries);

    onClick({
      productId,
      id,
      q,
      queries,
      fn: callback,
      fnWithId: callbackWithId,
    });
    if (setPage) {
      setPage(currentPaginationPage + 1);
    } else {
      saveCurrentPaginationPage(currentPaginationPage + 1);
    }
    setIsLoadBtnClick(false);
    setIsLabelRender(false);
  }, [
    currentPaginationPage,
    sortQuery,
    id,
    productId,
    q,
    additionalQuery,
    onClick,
    callback,
    callbackWithId,
  ]);

  const handleNumClick: (num: number | string) => () => void = num => () => {
    if (typeof num === 'string') return;

    const queries = [
      sortQuery,
      `page=${num}`,
      filtersQueries,
      switchQueries,
      priceQueries,
      additionalQuery ?? '',
    ];

    saveCurrentQueries(queries);

    onClick({
      productId,
      id,
      q,
      queries,
      fn: callback,
      fnWithId: callbackWithId,
    });
    if (setPage) {
      setPage(num);
    } else {
      saveCurrentPaginationPage(num);
    }
    setIsLoadBtnClick(false);
    setIsLabelRender(false);
  };

  const handleLoadMoreClick = useCallback(() => {
    const queries = [
      sortQuery,
      `page=${currentPaginationPage + 1}`,
      filtersQueries,
      switchQueries,
      priceQueries,
      additionalQuery ?? '',
    ];

    saveCurrentQueries(queries);

    onBtnLoadClick({
      productId,
      id,
      q,
      queries,
      fn: callback,
      fnWithId: callbackWithId,
    });
    if (setPage) {
      setPage(currentPaginationPage + 1);
    } else {
      saveCurrentPaginationPage(currentPaginationPage + 1);
    }
    setIsLoadBtnClick(true);
    setIsLabelRender(false);
  }, [
    currentPaginationPage,
    id,
    productId,
    q,
    sortQuery,
    filtersQueries,
    switchQueries,
    priceQueries,
    additionalQuery,
    onBtnLoadClick,
    callback,
    callbackWithId,
  ]);

  useEffect(() => {
    if (!isLoadBtnClick) {
      handleScroll();
    }
  }, [currentPaginationPage, isLoadBtnClick]);

  return {
    currentPaginationPage,
    handlePrevClick,
    handleNextClick,
    handleNumClick,
    handleLoadMoreClick,
  };
};
